import React, { useEffect, useState } from "react";
import "./FileDisplay.css";
import { IconButton, Stack, Spinner } from "@fluentui/react";
import { historyGetCreateFile } from "../../api";
import * as XLSX from "xlsx";
import PdfViewerWithPagination from "./PdfViewerWithPagination";
import TableViewerWithPagination from "./TableViewerWithPagination";

// ファイルデータのキャッシュ（blobFileName -> base64データ）
const fileDataCache = new Map<string, string>();

interface FileDisplayProps {
  blobFileName: string; // BlobStorageファイル名
  fileName: string; // ファイル名
  fileAreaMaxHeight: string;
  imgFileAlert?: string;
}

type FileType = "Image" | "Table" | "PDF" | "Text" | "Unknown";

const FileDisplay: React.FC<FileDisplayProps> = ({
  blobFileName,
  fileName,
  fileAreaMaxHeight, imgFileAlert,
}) => {
  const [fileContent, setFileContent] = useState<string | any[][]>(""); // テキストファイルやCSV/Excelの内容
  const [fileBase64, setFileBase64] = useState<string>(""); // ダウンロード用のBase64
  const [fileType, setFileType] = useState<FileType | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // ローディング表示

  useEffect(() => {
    setLoading(true);
    setFileType(null);

    const fetchFile = async () => {
      // キャッシュをチェック
      let fileData: string;
      if (fileDataCache.has(blobFileName)) {
        fileData = fileDataCache.get(blobFileName)!;
      } else {
        // ファイルデータを取得してキャッシュに保存
        fileData = await historyGetCreateFile(blobFileName);
        fileDataCache.set(blobFileName, fileData);
      }
      setFileBase64(fileData);

      // ファイルタイプを判別
      if (fileName.match(/\.(png|jpg|jpeg|gif|bmp)$/i)) {
        setFileType("Image");
        const url = `data:image/png;base64,${fileData}`;
        setFileContent(url); // 画像表示用
      } else if (fileName.match(/\.(csv)$/i)) {
        // CSVファイルをパース
        setFileType("Table");
        const decodedText = decodeText(fileData);
        const rows: any[][] = decodedText
          .split("\n")
          .filter((row) => row.trim() !== "")
          .map((row) => row.split(","));
        setFileContent(rows); // 明示的に型キャスト
      } else if (fileName.match(/\.(xlsx)$/i)) {
        // Excelファイルをパース
        setFileType("Table");
        const binary = atob(fileData);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const workbook = XLSX.read(bytes, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const sheetData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
        }) as any[][];
        let rows: any[][] = [];
        const maxCols = worksheet["!ref"]
          ? XLSX.utils.decode_range(worksheet["!ref"]).e.c + 1
          : 0;
        sheetData.forEach((row, rowIndex) => {
          let newRow: any[] = [];
          for (let colIndex = 0; colIndex < maxCols; colIndex++) {
            // 空白セルを補完
            newRow[colIndex] = row[colIndex] === undefined ? "" : row[colIndex];
          }
          rows.push(newRow);
        });
        setFileContent(rows);
      } else if (fileName.match(/\.(pdf)$/i)) {
        setFileType("PDF");
        const byteCharacters = atob(fileData);
        const byteNumbers = new Array(byteCharacters.length)
          .fill(0)
          .map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const pdfBlob = new Blob([byteArray], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setFileContent(pdfUrl);
      } else if (isText(fileData)) {
        setFileType("Text");
        // このままだと文字化けするので、UTF-8に変換
        const decodedText = decodeText(fileData);
        setFileContent(decodedText); // テキスト表示用
      } else {
        setFileType("Unknown");
        setFileContent("");
      }

      setLoading(false);
    };

    fetchFile();

    return () => {
      URL.revokeObjectURL(fileContent as string);
    };
  }, [blobFileName, fileName]);

  const handleDownload = () => {
    const byteCharacters = atob(fileBase64);
    const byteNumbers = Array.from(byteCharacters).map((char) =>
      char.charCodeAt(0),
    );
    const byteArray = new Uint8Array(byteNumbers);

    // Blobを作成し、ダウンロードを実行
    const blob = new Blob([byteArray]);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName || "downloaded-file";
    link.click();
  };

  const decodeText = (base64: string, encoding: string = "utf-8") => {
    const binary = atob(base64); // Base64デコード
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const decoder = new TextDecoder(encoding); // 指定されたエンコーディングでデコード
    return decoder.decode(bytes);
  };

  const isText = (base64: string): boolean => {
    // ファイルサイズが大きすぎる場合
    if (1024000 < base64.length) {
      return false;
    }
    try {
      // 先頭部分だけデコードして判定（パフォーマンスのため最大8KB分）
      const sampleBase64 = base64.slice(0, 10920); // ~8KB
      const binary = atob(sampleBase64);
      const length = binary.length;
      for (let i = 0; i < length; i++) {
        const code = binary.charCodeAt(i);
        // NULL バイトが含まれていればバイナリと判定
        if (code === 0) return false;
        // 制御文字（タブ・改行・CRを除く）が多ければバイナリ
        if (code < 0x08 || (code > 0x0d && code < 0x20 && code !== 0x1b)) {
          return false;
        }
      }
      return true;
    } catch {
      return false;
    }
  };

  return (
    <div className="file-container">
      {/* ツールエリア */}
      <div className="tool-area">
        <Stack tokens={{childrenGap: "10px"}}>
          <span className="file-name">{fileName}</span>
          {
            imgFileAlert && fileType == "Image" && <span className="imgDisclaimer">{imgFileAlert}</span>
          }
        </Stack>
        <IconButton
          className="itemButton"
          iconProps={{ iconName: "Download" }}
          title="ダウンロード"
          onClick={handleDownload}
          disabled={loading}
        />
      </div>

      {/* ファイル内容を表示 */}
      <div className="file-area" style={{ maxHeight: fileAreaMaxHeight }}>
        {loading ? (
          <div
            style={{
              padding: "10px",
            }}
          >
            <Spinner />
          </div>
        ) : fileType === "Image" ? (
          <img
            src={fileContent as string}
            alt={fileName}
            className="displayed-file"
          />
        ) : fileType === "Table" ? (
          <TableViewerWithPagination data={fileContent as any[][]} />
        ) : fileType === "PDF" ? (
          <PdfViewerWithPagination pdfData={fileContent as string} />
        ) : fileType === "Text" ? (
          <pre className="text-file-content">{fileContent as string}</pre>
        ) : (
          <pre className="text-file-content">表示できないファイルです</pre>
        )}
      </div>
    </div>
  );
};

export default FileDisplay;
